import React, { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { Box, IconButton, Menu, MenuItem, TextField, Tooltip, Typography, alpha } from "@mui/material";
import { ArrowUp, Square, Mic, Paperclip, ListChecks, Hourglass, Plus } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useChatContext } from "./providers/ChatProvider";
import { TurnStatus } from "../../chat/hooks/useStreamingTurn";
import { ModelPicker } from "./Settings/ModelPicker";
import { ComposerProjectBadge } from "./ComposerProjectBadge";
import type { AvailableCommandEntry } from "../../chat/events";
import { CommandPalette, filterCommands } from "./Commands/CommandPalette";
import { CHAT_COLUMN_MAX_WIDTH, CHAT_COLUMN_GUTTER } from "./layout";
import { tactileSpring } from "./motion";
import { useAttachments } from "../../chat/hooks/useAttachments";
import { AttachmentChip } from "./Attachments/AttachmentChip";
import { DropZone } from "./Attachments/DropZone";
import { tokens } from "@/theme/tokens";

/** Returns the slash-command filter if the composer's content is a
 *  single line starting with `/`. Returns null when the palette should
 *  be closed (multiple lines, doesn't start with `/`, etc.). */
function slashFilter(text: string): string | null {
  // Open only when the entire composer is one `/`-prefixed line.
  // Multi-line drafts (Shift+Enter) or `/` mid-line never trigger.
  if (!text.startsWith("/")) return null;
  if (text.includes("\n")) return null;
  return text.slice(1);
}

export interface ComposerProps {
  /** Opens voice mode. Wired by Spec B; when absent the mic is inert. */
  onVoice?: () => void;
  /** Centered new-chat placement: drop the top border + band padding so the
   *  pill sits cleanly under the greeting instead of as a bottom bar. */
  centered?: boolean;
}

export function Composer({ onVoice, centered = false }: ComposerProps) {
  const { chat, slots, selectedModel, projectId, features, documentsApi, composerDraft, prefillNonce, setComposerDraft } = useChatContext();
  const [text, setText] = useState("");

  // Consume a one-shot prefill (suggested prompt click). Apply the draft into
  // local state, clear the shared slot, and focus the input so the user can
  // edit or just press Enter. Keyed on the nonce so repeat clicks re-apply.
  useEffect(() => {
    if (prefillNonce === 0 || !composerDraft) return;
    setText(composerDraft);
    setHighlightIndex(0);
    setComposerDraft("");
    // Focus the textarea after the value lands.
    const el = textFieldRef.current?.querySelector("textarea");
    if (el) {
      el.focus();
      const len = composerDraft.length;
      el.setSelectionRange(len, len);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillNonce]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  // Plan-mode toggle: when on, the next message enters blocking plan mode
  // (payload.mode=plan). One-shot — reset after send so it doesn't stick.
  const [planMode, setPlanMode] = useState(false);
  // "Run in background" toggle: the next message tells the model to spawn
  // subagents fire-and-forget (no inline wait), so the turn ends immediately
  // and work is tracked on the tasks page. One-shot.
  const [backgroundMode, setBackgroundMode] = useState(false);
  // Anchor for the collapsed "+" actions menu (actionsMenu feature flag).
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const textFieldRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // A budget-paused turn is held in Preparing/Streaming for Continue/Stop, but
  // nothing is running: a send then is a new turn (sendMessage resolves the
  // pause as Stop), not a steer queued for a turn that never starts
  // (ELASV1-3514).
  const streaming =
    (chat.turn.status === TurnStatus.Streaming || chat.turn.status === TurnStatus.Preparing) &&
    !chat.turn.budgetPause;
  const reduce = useReducedMotion() ?? false;

  const attachments = useAttachments({ api: documentsApi });

  // SP-3b: derive palette open-state from composer text. The palette
  // closes automatically when the user types a non-`/` first char or
  // adds a newline.
  const filter = slashFilter(text);
  const paletteOpen = filter !== null && chat.availableCommands.length > 0;
  const filteredCommands = useMemo(
    () =>
      filter === null ? [] : filterCommands(chat.availableCommands, filter),
    [filter, chat.availableCommands],
  );

  // Keep highlight in range as the filter narrows results.
  if (paletteOpen && highlightIndex >= filteredCommands.length) {
    setHighlightIndex(0);
  }

  const send = async () => {
    const t = text.trim();
    if ((!t && attachments.attachmentIds.length === 0) || attachments.uploading) return;
    // While streaming: route to steer (mid-turn injection) instead of sendMessage.
    if (streaming) {
      if (!t) return;
      try {
        await chat.steer(t);
        setText("");
        setHighlightIndex(0);
      } catch (e) {
        // keep the text so the user can retry
        console.warn("[Composer] steer failed", e);
      }
      return;
    }
    setText("");
    setHighlightIndex(0);
    // Send the picker value verbatim on every message. "auto" (sent
    // explicitly, not omitted) tells the backend to run the cost-router
    // classifier; a specific id is used as-is with no classification.
    const model = selectedModel || "auto";
    const ids = attachments.attachmentIds;
    // Capture display info (name/mime) for the chips on the user bubble BEFORE
    // clearing — the backend persists the same so it survives reload.
    const attachmentDisplay = attachments.attachments
      .filter((a) => a.status === "ready" && a.documentId != null)
      .map((a) => ({ id: String(a.documentId), name: a.name, mime: a.mime }));
    attachments.clear();
    const usePlan = planMode;
    const useBackground = backgroundMode;
    setPlanMode(false); // one-shot
    setBackgroundMode(false); // one-shot
    await chat.sendMessage({
      text: t,
      model,
      project_id: projectId,
      attachment_ids: ids.length ? ids : undefined,
      attachments: attachmentDisplay.length ? attachmentDisplay : undefined,
      ...(usePlan ? { mode: "plan" as const } : {}),
      ...(useBackground ? { backgroundSubagents: true } : {}),
    });
  };

  const runCommand = async (cmd: AvailableCommandEntry) => {
    if (streaming) return;
    setText("");
    setHighlightIndex(0);
    // Slash commands don't carry attachments — discard silently to avoid
    // lingering chips and the uploading guard blocking the next send.
    attachments.clear();
    const model = selectedModel || "auto";
    // SP-3b auto-submit: the script's prompt_template becomes the next
    // user message immediately (per the spec).
    await chat.sendMessage({
      text: cmd.prompt_template,
      model,
      project_id: projectId,
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Palette keyboard nav takes precedence over the default send.
    if (paletteOpen && filteredCommands.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex(
          (i) => (i - 1 + filteredCommands.length) % filteredCommands.length,
        );
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const cmd = filteredCommands[highlightIndex];
        if (cmd) runCommand(cmd);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setText("");
        return;
      }
      if (e.key === "Tab") {
        // Tab completes the highlighted command name into the composer
        // (lets the user verify before pressing Enter).
        e.preventDefault();
        const cmd = filteredCommands[highlightIndex];
        if (cmd) setText(`/${cmd.namespaced_name}`);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Filled TERRACOTTA send button — the primary action color, prominent with a
  // soft extruded glow. Presses inward on click; disabled reads as recessed.
  const sendButtonSx = {
    // Accent-filled send button (follows the selected theme), flat — no colored
    // halo. Hover darkens the fill; active presses inward.
    bgcolor: "primary.main",
    color: "primary.contrastText",
    width: 40,
    height: 40,
    // Icon-only controls read as circles (distinct from the tightened radius
    // scale, which is for labeled buttons/cards) — the pre-refresh look, and
    // the common chat-app convention (ChatGPT/Claude send buttons).
    borderRadius: "50%",
    boxShadow: "none",
    transition: "box-shadow 160ms ease, background-color 160ms ease, transform 120ms ease",
    "&:hover": { bgcolor: "primary.main", filter: "brightness(0.94)" },
    "&:active": { transform: "scale(0.96)", boxShadow: "inset 2px 2px 6px rgba(0,0,0,0.25)" },
    // Dormant: a faint accent tint (not cream) so it reads as the resting send
    // button, then fills solid accent the moment there's something to send.
    "&.Mui-disabled": {
      bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.primary.main, 0.1),
      color: "action.disabled",
      boxShadow: "none",
    },
  } as const;

  // Quiet ghost icon buttons for the toolbar (attach / voice / toggles). No
  // resting fill — just a muted glyph on the tinted composer surface — so the
  // filled send button stays the sole point of emphasis. Hover/press bloom into
  // the accent tint (same language as the plan/background toggle-on state).
  const iconBtnSx = {
    color: "text.secondary",
    width: 36,
    height: 36,
    // Circular, matching the send button — icon-only controls, not cards.
    borderRadius: "50%",
    bgcolor: "transparent",
    boxShadow: "none",
    transition: "background-color 160ms ease, color 160ms ease, transform 120ms ease",
    "&:hover": {
      color: "primary.main",
      bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.primary.main, 0.12),
    },
    "&:active": {
      transform: "scale(0.94)",
      bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.primary.main, 0.18),
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "background-color 160ms ease, color 160ms ease",
      "&:active": { transform: "none" },
    },
  } as const;

  // When the pill lives inside the DropZone wrapper (which is already
  // capped to CHAT_COLUMN_MAX_WIDTH), it fills that wrapper as width:100%.
  // When rendered standalone the pill carries its own maxWidth cap.
  const innerPill = (cappedByParent = false) => (
    <Box
      sx={(t) => {
        const n = t.palette.mode === "light" ? tokens.neu.light : tokens.neu.dark;
        return {
          width: "100%",
          ...(cappedByParent ? {} : { maxWidth: CHAT_COLUMN_MAX_WIDTH }),
          // Crisp rounded-rectangle panel (NOT a pill) — reads as a deliberate
          // input surface, not a bubble.
          borderRadius: `${tokens.radius.lg}px`,
          // Accent-tinted surface — matches the global search selection, the
          // sidebar active item, and the starter cards (accent @ 8%). Tracks
          // the chosen accent since it derives from primary.main.
          bgcolor: alpha(t.palette.primary.main, 0.08),
          boxShadow: n.raised,
          overflow: "hidden",
          transition: "box-shadow 180ms ease",
          // On focus, a soft accent ring so the whole composer reads as active.
          "&:focus-within": {
            boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.35)}`,
          },
        };
      }}
    >
      {/* Attachment chips row above the input — only when attachments feature is on and there are chips */}
      {features.attachments && attachments.attachments.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, px: 2, pt: 1.5 }}>
          {attachments.attachments.map((a) => (
            <AttachmentChip
              key={a.localId}
              name={a.name}
              status={a.status}
              mime={a.mime}
              error={a.error}
              onRemove={() => attachments.remove(a.localId)}
              onPromote={
                a.documentId != null
                  ? () => {
                      void documentsApi.promote(a.documentId!).catch(() => {
                        /* soft-fail: promotion is best-effort */
                      });
                    }
                  : undefined
              }
            />
          ))}
        </Box>
      )}
      {/* Input is the hero, on top. Larger + taller in the centered new-chat
          placement so it reads as the primary call-to-action. */}
      <Box sx={{ px: 2.5, pt: centered ? 2.25 : 1.75, pb: 0.5 }}>
        <TextField
          ref={textFieldRef}
          multiline
          minRows={centered ? 2 : 1}
          maxRows={centered ? 12 : 6}
          fullWidth
          variant="standard"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setHighlightIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Ask Artie ..."
          slotProps={{
            htmlInput: { "aria-label": "message" },
            input: {
              disableUnderline: true,
              sx: {
                fontSize: centered ? "1.08rem" : "0.98rem",
                lineHeight: 1.6,
                fontWeight: 500,
              },
            },
          }}
        />
      </Box>
      {/* Single control toolbar below the input. Extreme left: context (model
          picker + project badge). Extreme right: action buttons + send.
          The composer also lives in narrow embed panels on wide viewports, so
          this row can't lean on viewport breakpoints — it degrades on its own
          width instead: when the two groups can't share a line the action
          cluster drops to a second row (still right-aligned), and below that
          the chip labels truncate. Previously they wrapped mid-label and
          printed over the buttons. */}
      <Box
        data-composer-toolbar="true"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 0.5,
          rowGap: 0.5,
          px: 1.25,
          pb: 1.25,
          pt: 0.25,
        }}
      >
        <Box
          data-composer-context="true"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}
        >
          <ModelPicker />
          <ComposerProjectBadge />
        </Box>
        <Box
          data-composer-actions="true"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, ml: "auto" }}
        >
          {slots.composerLeft}
          {features.attachments && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  const fs = Array.from(e.target.files ?? []);
                  if (fs.length) attachments.add(fs);
                  e.target.value = "";
                }}
              />
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                aria-label="attach files"
                size="small"
                sx={iconBtnSx}
              >
                <Paperclip size={18} />
              </IconButton>
            </>
          )}
          {onVoice && (
            <IconButton onClick={onVoice} aria-label="voice mode" size="small" sx={iconBtnSx}>
              <Mic size={18} />
            </IconButton>
          )}
          {features.actionsMenu ? (
            // Collapsed mode: single "+" opens a menu with plan/background items.
            // Only render the button when at least one action is enabled.
            (features.planMode || features.backgroundSubagents) && (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    onClick={(e) => setActionsAnchor(e.currentTarget)}
                    aria-label="more actions"
                    aria-haspopup="true"
                    size="small"
                    sx={{
                      ...iconBtnSx,
                      ...((planMode || backgroundMode)
                        ? { color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }
                        : {}),
                    }}
                  >
                    <Plus size={18} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={actionsAnchor}
                  open={Boolean(actionsAnchor)}
                  onClose={() => setActionsAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                  {features.planMode && (
                    <MenuItem
                      onClick={() => { setPlanMode((v) => !v); setActionsAnchor(null); }}
                      selected={planMode}
                    >
                      <ListChecks size={16} style={{ marginRight: 8 }} />
                      {planMode ? "Plan mode on" : "Plan first"}
                    </MenuItem>
                  )}
                  {features.backgroundSubagents && (
                    <MenuItem
                      onClick={() => { setBackgroundMode((v) => !v); setActionsAnchor(null); }}
                      selected={backgroundMode}
                    >
                      <Hourglass size={16} style={{ marginRight: 8 }} />
                      {backgroundMode ? "Background on" : "Run in background"}
                    </MenuItem>
                  )}
                </Menu>
              </>
            )
          ) : (
            <>
              {features.planMode && (
                <Tooltip title={planMode ? "Plan mode on — I'll propose a plan for approval first" : "Plan first (propose a plan before acting)"}>
                  <IconButton
                    onClick={() => setPlanMode((v) => !v)}
                    aria-label="plan mode"
                    aria-pressed={planMode}
                    size="small"
                    sx={{
                      ...iconBtnSx,
                      ...(planMode
                        ? { color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }
                        : {}),
                    }}
                  >
                    <ListChecks size={18} />
                  </IconButton>
                </Tooltip>
              )}
              {features.backgroundSubagents && (
                <Tooltip title={backgroundMode ? "Background on — I'll start the task and you can track it on the Schedules page" : "Run in background (start the task, don't wait — track it on the Schedules page)"}>
                  <IconButton
                    onClick={() => setBackgroundMode((v) => !v)}
                    aria-label="run in background"
                    aria-pressed={backgroundMode}
                    size="small"
                    sx={{
                      ...iconBtnSx,
                      ...(backgroundMode
                        ? { color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.12) }
                        : {}),
                    }}
                  >
                    <Hourglass size={18} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {/* Web search is always on (Artie runs it automatically as a tool), so
              there's no indicator or toggle here — it would only be clutter. */}
          {streaming ? (
            <>
              <motion.span whileTap={{ scale: reduce ? 1 : 0.9 }} transition={tactileSpring} style={{ display: "inline-flex" }}>
                <IconButton onClick={() => chat.cancel()} aria-label="stop" sx={iconBtnSx}><Square size={18} /></IconButton>
              </motion.span>
              <motion.span whileTap={{ scale: reduce ? 1 : 0.9 }} transition={tactileSpring} style={{ display: "inline-flex" }}>
                <IconButton
                  onClick={send}
                  aria-label="send"
                  disabled={text.trim().length === 0}
                  sx={sendButtonSx}
                >
                  <ArrowUp size={18} />
                </IconButton>
              </motion.span>
            </>
          ) : (
            <motion.span whileTap={{ scale: reduce ? 1 : 0.9 }} transition={tactileSpring} style={{ display: "inline-flex" }}>
              <IconButton
                onClick={send}
                aria-label="send"
                disabled={(text.trim().length === 0 && attachments.attachmentIds.length === 0) || attachments.uploading}
                sx={sendButtonSx}
              >
                <ArrowUp size={18} />
              </IconButton>
            </motion.span>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        // In centered (new-chat) placement the composer sits under the greeting
        // with no divider band; bottom-pinned placement keeps the top border.
        borderTop: 0, // Design looks floating without strict line
        borderColor: "divider",
        // Transparent — only the rounded pill paints a surface. A filled
        // wrapper here reads as a square panel behind the composer.
        bgcolor: "transparent",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        // In centered (new-chat) placement the parent already provides the
        // horizontal inset, so adding our own here would shrink the pill
        // narrower than the suggestion chips below it (width mismatch). Only
        // pad in the bottom-pinned placement.
        px: centered ? 0 : CHAT_COLUMN_GUTTER,
        py: centered ? 0 : 1.5,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: CHAT_COLUMN_MAX_WIDTH, display: "flex", flexDirection: "column", gap: 1 }}>
        {features.attachments ? (
          <DropZone onFiles={(files) => attachments.add(files)}>
            {innerPill(true)}
          </DropZone>
        ) : (
          innerPill(true)
        )}
        <Typography variant="caption" sx={{ color: "text.disabled", textAlign: "center", display: "block", fontSize: 11 }}>
          Artie can make mistakes. Consider verifying critical information.
        </Typography>
      </Box>
      <CommandPalette
        anchorEl={paletteOpen ? textFieldRef.current : null}
        commands={chat.availableCommands}
        filter={filter ?? ""}
        highlightIndex={highlightIndex}
        onSelect={runCommand}
        onDismiss={() => setText("")}
      />
    </Box>
  );
}
